import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, ArrowLeft, ArrowRight, Check, Loader2, Mail } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import FileUpload from "@/components/FileUpload";
import { toast } from "sonner";
import { COUNTRIES, LANGUAGES, DEVICE_TYPES, MANUFACTURERS, OS_VERSIONS, ADDITIONAL_DEVICES, DAYS, TIME_SLOTS } from "@/lib/countries";
import { Country as CSCCountry, State as CSCState, City as CSCCity } from "country-state-city";
import DeviceInventory from "@/components/DeviceInventory";

// Full country list from country-state-city (250 countries with ISO codes) — used to
// power the country + city dropdowns on Step 2. Fall back to legacy COUNTRIES only
// if the library ever fails to load.
const ALL_COUNTRIES = CSCCountry.getAllCountries().map(c => ({ name: c.name, iso: c.isoCode }));

const SKILLS = ["functional", "exploratory", "regression", "usability", "accessibility", "API", "automation", "AI testing", "LLM evaluation", "red teaming"];
const TOOLS = ["Jira", "TestRail", "Zephyr", "Xray", "BrowserStack", "Postman", "Playwright", "Selenium", "Charles Proxy"];
const DOMAINS = ["banking", "ecommerce", "healthcare", "telecom", "SaaS", "gaming", "AI/ML", "fintech"];
const STEPS = [
  "Account", "Location & languages", "Skills", "Devices", "Tools", "Domains", "Availability & payment", "Agreements & documents",
];

function Toggle({ value, options, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map(opt => {
        const active = value.includes(opt);
        return (
          <button key={opt} type="button" onClick={() => onChange(active ? value.filter(v=>v!==opt) : [...value, opt])}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}
            data-testid={`toggle-${opt.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
            {active && <Check className="w-3 h-3 inline mr-1" />} {opt}
          </button>
        );
      })}
    </div>
  );
}

function MultiSelectDropdown({ value, options, onChange, placeholder = "Select…", testid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <div className="relative" data-testid={testid} ref={ref}>
      <button type="button" onClick={() => setOpen(o=>!o)}
        className="w-full text-left border border-slate-300 rounded-md px-3 py-2 text-sm bg-white hover:border-slate-400 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500">
        {value.length === 0 ? <span className="text-slate-400">{placeholder}</span> : <span className="text-slate-900">{value.length} selected</span>}
        <span className="float-right text-slate-400">▾</span>
      </button>
      {open && (
        <div className="absolute z-10 mt-1 w-full max-h-64 overflow-auto bg-white border border-slate-200 rounded-md shadow-lg">
          {options.map(opt => {
            const active = value.includes(opt);
            return (
              <button key={opt} type="button" onClick={() => onChange(active ? value.filter(v=>v!==opt) : [...value, opt])}
                className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2">
                <Checkbox checked={active} className="pointer-events-none" />
                <span>{opt}</span>
              </button>
            );
          })}
          <div className="sticky bottom-0 bg-white border-t border-slate-200 p-2 flex justify-end">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="px-3 py-1.5 text-xs font-semibold bg-slate-900 text-white rounded hover:bg-slate-800"
              data-testid={testid ? `${testid}-done` : "multiselect-done"}
            >
              Done
            </button>
          </div>
        </div>
      )}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {value.map(v => (
            <span key={v} className="pill bg-cyan-50 text-cyan-700 border-cyan-200">
              {v}
              <button type="button" onClick={() => onChange(value.filter(x=>x!==v))} className="ml-1 text-cyan-700 hover:text-cyan-900">×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Register() {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const nav = useNavigate();
  const [f, setF] = useState({
    name: "", first_name: "", last_name: "", email: "", password: "", password_confirm: "", phone: "",
    avatar_file_id: "", avatar_file_name: "",
    country: "", state: "", city: "", timezone: "UTC",
    languages: [],
    years_experience: 1,
    testing_skills: [],
    custom_skills: "",
    device_types: [],
    device_manufacturers: [],
    os_versions: [],
    additional_devices: [],
    devices: "",
    device_inventory: [],
    tools: [],
    custom_tools: "",
    domains: [],
    custom_domains: "",
    availability_hours: 10,
    availability_schedule: [],
    payment_method: "PayPal",
    payment_id: "",
    nda_accepted: false,
    code_of_conduct_accepted: false,
    privacy_accepted: false,
    resume_file_id: "",
    resume_file_name: "",
    national_id_file_id: "",
    national_id_file_name: "",
    portfolio_url: "",
    linkedin_url: "",
  });
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const next = () => setStep(s => Math.min(STEPS.length - 1, s + 1));
  const back = () => setStep(s => Math.max(0, s - 1));

  const setAvailability = (day, slot) => {
    const others = f.availability_schedule.filter(a => a.day !== day);
    if (slot && slot !== "Not available") others.push({ day, slot });
    set("availability_schedule", others);
  };
  const slotFor = (day) => f.availability_schedule.find(a => a.day === day)?.slot || "Not available";

  // Per-step validation
  const stepValid = () => {
    if (step === 0) return f.first_name.trim() && f.last_name.trim() && f.email && f.password.length >= 8 && f.password === f.password_confirm;
    if (step === 1) {
      if (!f.country || !f.city) return false;
      const iso = (ALL_COUNTRIES.find(c => c.name === f.country) || {}).iso;
      const states = iso ? CSCState.getStatesOfCountry(iso) || [] : [];
      if (states.length > 0 && !f.state) return false;
      return true;
    }
    if (step === 3) return (f.device_inventory || []).length > 0;
    if (step === 6) return f.payment_method && f.payment_id;
    if (step === 7) return f.nda_accepted && f.code_of_conduct_accepted && f.privacy_accepted;
    return true;
  };

  const submit = async () => {
    if (!stepValid()) { toast.error("Please complete all required fields and accept the agreements."); return; }
    setBusy(true);
    try {
      const stateIso = f.state;
      const countryIso = (ALL_COUNTRIES.find(c => c.name === f.country) || {}).iso;
      const stateName = (countryIso && stateIso)
        ? (CSCState.getStateByCodeAndCountry(stateIso, countryIso)?.name || stateIso)
        : "";
      const payload = {
        ...f,
        state: stateName,
        os_versions: f.os_versions,
        // hours per week derived from schedule if user filled it
        availability_hours: Number(f.availability_hours) || 0,
      };
      delete payload.resume_file_name;
      delete payload.avatar_file_name;
      delete payload.national_id_file_name;
      delete payload.devices;
      const { data } = await api.post("/auth/register-freelancer", payload);
      setSubmitted(data);
      toast.success("Application submitted!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed");
    } finally { setBusy(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-md p-8 text-center" data-testid="signup-success">
          <div className="w-14 h-14 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-6 h-6" />
          </div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Check your inbox</h1>
          <p className="text-slate-600 mt-2 text-sm">
            We've sent a welcome email to <b>{f.email}</b>. Click the confirmation link inside to verify your account.
            Once confirmed, our admin team will review your application — you'll be notified by email when you're approved.
          </p>
          {submitted.confirm_link_preview && (
            <div className="mt-5 p-3 border border-cyan-200 bg-cyan-50 rounded-md text-xs text-cyan-900 text-left" data-testid="dev-confirm-link">
              <div className="font-semibold mb-1">Development mode</div>
              Resend isn't configured. Use this link to confirm:
              <a href={submitted.confirm_link_preview} className="block text-cyan-700 underline break-all mt-1 font-mono">{submitted.confirm_link_preview}</a>
            </div>
          )}
          <Button onClick={()=>nav("/login")} className="mt-6 w-full bg-slate-900 hover:bg-slate-800" data-testid="btn-go-login">Go to sign in</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-900">
            <div className="w-7 h-7 bg-slate-950 rounded-sm flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-cyan-400" strokeWidth={2.5} />
            </div>
            <div className="font-display font-bold">CogniTrust<span className="text-cyan-600">.</span>AI</div>
          </Link>
          <Link to="/login" className="text-sm text-slate-600 hover:text-slate-900" data-testid="link-login-from-register">Already have an account?</Link>
        </div>
      </div>

      <div className="flex-1 max-w-3xl mx-auto w-full px-6 py-8">
        <div className="mb-8" data-testid="wizard-steps">
          <div className="flex items-center">
            {STEPS.map((s, i) => (
              <div key={i} className="flex items-center flex-1 last:flex-none">
                <div className={`step-dot ${i === step ? "active" : i < step ? "done" : ""}`}>{i < step ? <Check className="w-3.5 h-3.5"/> : i+1}</div>
                {i < STEPS.length - 1 && <div className={`step-line ${i < step ? "done" : ""}`} />}
              </div>
            ))}
          </div>
          <div className="mt-3 text-[11px] uppercase tracking-wider text-cyan-700 font-medium">Step {step+1} of {STEPS.length}</div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-950 mt-1">{STEPS[step]}</h1>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-6 md:p-8" data-testid={`wizard-step-${step}`}>
          {step === 0 && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="First name" required>
                  <Input
                    value={f.first_name}
                    onChange={e=>{
                      const first = e.target.value;
                      set("first_name", first);
                      set("name", `${first} ${f.last_name}`.trim());
                    }}
                    placeholder="Jane"
                    autoComplete="given-name"
                    data-testid="input-first-name"
                  />
                </Field>
                <Field label="Last name" required>
                  <Input
                    value={f.last_name}
                    onChange={e=>{
                      const last = e.target.value;
                      set("last_name", last);
                      set("name", `${f.first_name} ${last}`.trim());
                    }}
                    placeholder="Doe"
                    autoComplete="family-name"
                    data-testid="input-last-name"
                  />
                </Field>
              </div>
              <Field label="Email" required>
                <Input type="email" value={f.email} onChange={e=>set("email", e.target.value)} placeholder="jane@example.com" autoCapitalize="none" autoCorrect="off" autoComplete="email" inputMode="email" enterKeyHint="next" data-testid="input-email" />
              </Field>
              <Field label="Password" required hint="8+ characters">
                <Input type="password" value={f.password} onChange={e=>set("password", e.target.value)} placeholder="••••••••" autoCapitalize="none" autoCorrect="off" autoComplete="new-password" enterKeyHint="next" data-testid="input-password" />
              </Field>
              <Field label="Confirm password" required hint="Re-enter the same password to confirm">
                <Input
                  type="password"
                  value={f.password_confirm}
                  onChange={e=>set("password_confirm", e.target.value)}
                  placeholder="••••••••"
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoComplete="new-password"
                  enterKeyHint="next"
                  data-testid="input-password-confirm"
                  aria-invalid={!!(f.password_confirm && f.password !== f.password_confirm)}
                  className={f.password_confirm && f.password !== f.password_confirm ? "border-red-400 focus-visible:ring-red-400" : undefined}
                />
                {f.password_confirm && f.password !== f.password_confirm && (
                  <p className="mt-1 text-xs text-red-600" data-testid="err-password-mismatch">
                    Passwords do not match.
                  </p>
                )}
              </Field>
              <Field label="Phone">
                <Input value={f.phone} onChange={e=>set("phone", e.target.value)} placeholder="+1 555 0100" data-testid="input-phone" />
              </Field>
              <Field label="Profile picture (optional)" hint="JPG/PNG, max 10MB">
                <FileUpload accept="image/*" testid="upload-avatar" authed={false}
                  label={f.avatar_file_name || "Click to upload a profile picture"}
                  onUploaded={(d)=>{ set("avatar_file_id", d.id); set("avatar_file_name", d.name); }} />
              </Field>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Country" required>
                  <Select value={f.country} onValueChange={(v)=>{ set("country", v); set("state", ""); set("city", ""); }}>
                    <SelectTrigger data-testid="select-country"><SelectValue placeholder="Select country"/></SelectTrigger>
                    <SelectContent className="max-h-80">
                      {ALL_COUNTRIES.map(({name}) => <SelectItem key={name} value={name}>{name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="State / Province" required>
                  {(() => {
                    const iso = (ALL_COUNTRIES.find(c => c.name === f.country) || {}).iso;
                    const states = iso ? CSCState.getStatesOfCountry(iso) || [] : [];
                    const hasStates = states.length > 0;
                    return (
                      <Select
                        value={f.state}
                        onValueChange={(v)=>{ set("state", v); set("city", ""); }}
                        disabled={!f.country || !hasStates}
                      >
                        <SelectTrigger data-testid="select-state">
                          <SelectValue placeholder={!f.country ? "Select country first" : (hasStates ? "Select state/province" : "N/A for this country")}/>
                        </SelectTrigger>
                        <SelectContent className="max-h-80">
                          {states.map(s => <SelectItem key={s.isoCode} value={s.isoCode}>{s.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    );
                  })()}
                </Field>
                <Field label="City" required>
                  {(() => {
                    const iso = (ALL_COUNTRIES.find(c => c.name === f.country) || {}).iso;
                    const states = iso ? CSCState.getStatesOfCountry(iso) || [] : [];
                    const hasStates = states.length > 0;
                    let cities = [];
                    if (iso) {
                      if (hasStates && f.state) {
                        cities = CSCCity.getCitiesOfState(iso, f.state) || [];
                      } else if (!hasStates) {
                        cities = CSCCity.getCitiesOfCountry(iso) || [];
                      }
                    }
                    const listId = iso ? `city-options-${iso}-${f.state || "all"}` : undefined;
                    const disabled = !f.country || (hasStates && !f.state);
                    const placeholder = !f.country
                      ? "Select country first"
                      : (hasStates && !f.state)
                        ? "Select state first"
                        : `Search ${cities.length.toLocaleString()} cities…`;
                    return (
                      <>
                        <Input
                          list={listId}
                          value={f.city}
                          onChange={(e)=>set("city", e.target.value)}
                          placeholder={placeholder}
                          disabled={disabled}
                          autoCapitalize="words"
                          autoComplete="off"
                          data-testid="input-city"
                        />
                        {listId && (
                          <datalist id={listId} data-testid="datalist-cities">
                            {cities.map(c => <option key={`${c.name}-${c.stateCode}`} value={c.name} />)}
                          </datalist>
                        )}
                      </>
                    );
                  })()}
                </Field>
              </div>
              <Field label="Languages spoken" hint="Select all that apply and press Done">
                {(() => {
                  const OTHER = "Other";
                  const knownLangs = new Set(LANGUAGES);
                  // Selected value that isn't in the built-in list is treated as a
                  // custom "Other" entry — we surface it in the text input below.
                  const customFromArray = (f.languages || []).find(l => !knownLangs.has(l) && l !== OTHER) || "";
                  const otherSelected = (f.languages || []).includes(OTHER) || !!customFromArray;
                  const shown = otherSelected && !((f.languages || []).includes(OTHER))
                    ? [...(f.languages || []).filter(l => l !== customFromArray), OTHER]
                    : (f.languages || []);
                  const options = [...LANGUAGES, OTHER];
                  return (
                    <>
                      <MultiSelectDropdown
                        value={shown}
                        options={options}
                        onChange={(v) => {
                          // Preserve any typed custom value when the picker isn't the
                          // one editing it.
                          if (v.includes(OTHER) && customFromArray) {
                            set("languages", [...v.filter(x => x !== OTHER), customFromArray]);
                          } else {
                            set("languages", v);
                          }
                        }}
                        placeholder="Choose languages"
                        testid="multi-languages"
                      />
                      {otherSelected && (
                        <div className="mt-2">
                          <Input
                            value={customFromArray}
                            onChange={(e) => {
                              const text = e.target.value;
                              // Replace the previous custom entry (or the "Other" marker)
                              // with the freshly typed value; empty text keeps "Other"
                              // selected so the input stays visible.
                              const base = (f.languages || []).filter(l => l !== OTHER && l !== customFromArray);
                              set("languages", text.trim() ? [...base, text] : [...base, OTHER]);
                            }}
                            placeholder="Type your language(s) — e.g. Farsi, Pashto"
                            data-testid="input-other-language"
                            autoCapitalize="words"
                          />
                          <p className="text-[11px] text-slate-500 mt-1">Type the language name and press Tab. Multiple? Separate with commas.</p>
                        </div>
                      )}
                    </>
                  );
                })()}
              </Field>
              <Field label="Years of QA / testing experience" required>
                <Input type="number" min={0} max={40} value={f.years_experience} onChange={e=>set("years_experience", Number(e.target.value))} inputMode="numeric" className="no-spinner" data-testid="input-years" />
              </Field>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <Field label="Testing skills" hint="Select all that apply">
                <Toggle value={f.testing_skills} options={SKILLS} onChange={(v)=>set("testing_skills", v)} testid="skills" />
              </Field>
              <Field label="Others (manually add)" hint="Comma separated, e.g. chaos testing, mutation testing">
                <Input value={f.custom_skills} onChange={e=>set("custom_skills", e.target.value)} placeholder="Other testing skills" data-testid="input-custom-skills" />
              </Field>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div>
                <div className="font-display text-xl font-bold text-slate-900" data-testid="devices-heading">Add Your Devices</div>
                <p className="text-sm text-slate-600 mt-1">
                  Add the devices you can test on. You can add multiple devices — this helps us match you to the right assignments.
                </p>
              </div>
              <DeviceInventory
                value={f.device_inventory}
                onChange={(list) => set("device_inventory", list)}
              />
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <Field label="Tools experience">
                <Toggle value={f.tools} options={TOOLS} onChange={(v)=>set("tools", v)} testid="tools" />
              </Field>
              <Field label="Others (manually add)" hint="Comma separated">
                <Input value={f.custom_tools} onChange={e=>set("custom_tools", e.target.value)} placeholder="Other tools" data-testid="input-custom-tools" />
              </Field>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-4">
              <Field label="Domain experience">
                <Toggle value={f.domains} options={DOMAINS} onChange={(v)=>set("domains", v)} testid="domains" />
              </Field>
              <Field label="Others (manually add)" hint="Comma separated">
                <Input value={f.custom_domains} onChange={e=>set("custom_domains", e.target.value)} placeholder="Other domains" data-testid="input-custom-domains" />
              </Field>
            </div>
          )}

          {step === 6 && (
            <div className="space-y-5">
              <Field label="Weekly availability (per day)" hint="Pick the time slot you're typically free each day.">
                <div className="space-y-2" data-testid="availability-grid">
                  {DAYS.map(d => (
                    <div key={d} className="flex items-center gap-3">
                      <div className="w-12 text-sm font-medium text-slate-700">{d}</div>
                      <Select value={slotFor(d)} onValueChange={(v)=>setAvailability(d, v)}>
                        <SelectTrigger className="flex-1" data-testid={`select-availability-${d}`}><SelectValue/></SelectTrigger>
                        <SelectContent>
                          {TIME_SLOTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </Field>
              <Field label="Total available hours per week" required>
                <Input
                  type="number"
                  min={0}
                  max={60}
                  value={f.availability_hours === "" || f.availability_hours === null || f.availability_hours === undefined ? "" : f.availability_hours}
                  onChange={e => {
                    const v = e.target.value;
                    set("availability_hours", v === "" ? "" : Number(v));
                  }}
                  onBlur={e => {
                    // On blur, if empty, leave it empty (user can clear). Otherwise clamp.
                    if (e.target.value === "") return;
                    const n = Number(e.target.value);
                    if (Number.isNaN(n)) { set("availability_hours", ""); return; }
                    if (n < 0) set("availability_hours", 0);
                    else if (n > 60) set("availability_hours", 60);
                  }}
                  inputMode="numeric"
                  className="no-spinner"
                  placeholder="e.g. 20"
                  data-testid="input-availability-hours"
                />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Preferred payment method" required>
                  <Select value={f.payment_method} onValueChange={(v)=>{ set("payment_method", v); set("payment_id", ""); }}>
                    <SelectTrigger data-testid="select-payment"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PayPal">PayPal</SelectItem>
                      <SelectItem value="Payoneer">Payoneer</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={`${f.payment_method} ID / email`} required>
                  <Input value={f.payment_id} onChange={e=>set("payment_id", e.target.value)}
                    placeholder={f.payment_method === "PayPal" ? "name@paypal-account.com" : "Payoneer ID"}
                    data-testid="input-payment-id" />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Portfolio URL"><Input value={f.portfolio_url} onChange={e=>set("portfolio_url", e.target.value)} placeholder="https://" data-testid="input-portfolio" /></Field>
                <Field label="LinkedIn URL"><Input value={f.linkedin_url} onChange={e=>set("linkedin_url", e.target.value)} placeholder="https://linkedin.com/in/..." data-testid="input-linkedin" /></Field>
              </div>
            </div>
          )}

          {step === 7 && (
            <div className="space-y-5">
              <Field label="Resume / profile document (PDF, max 10MB)">
                <FileUpload accept=".pdf,application/pdf" testid="upload-resume" authed={false}
                  label={f.resume_file_name || "Click to upload resume"}
                  onUploaded={(d) => { set("resume_file_id", d.id); set("resume_file_name", d.name); }} />
              </Field>
              <Field label="National ID / government-issued ID (image or PDF, max 10MB)" hint="Required for KYC verification.">
                <FileUpload accept="image/*,.pdf" testid="upload-national-id" authed={false}
                  label={f.national_id_file_name || "Click to upload National ID"}
                  onUploaded={(d) => { set("national_id_file_id", d.id); set("national_id_file_name", d.name); }} />
              </Field>
              <div className="border border-slate-200 rounded-md p-4 space-y-3 bg-slate-50/50">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Mandatory agreements</div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox checked={f.nda_accepted} onCheckedChange={(v)=>set("nda_accepted", !!v)} data-testid="checkbox-nda" />
                  <div className="text-sm">
                    <div className="font-medium text-slate-900">NDA acknowledged <span className="text-red-500">*</span></div>
                    <div className="text-slate-600 text-xs mt-0.5">I will keep client information confidential.</div>
                  </div>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox checked={f.code_of_conduct_accepted} onCheckedChange={(v)=>set("code_of_conduct_accepted", !!v)} data-testid="checkbox-conduct" />
                  <div className="text-sm">
                    <div className="font-medium text-slate-900">Code of Conduct <span className="text-red-500">*</span></div>
                    <div className="text-slate-600 text-xs mt-0.5">I agree to act professionally on all projects.</div>
                  </div>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox checked={f.privacy_accepted} onCheckedChange={(v)=>set("privacy_accepted", !!v)} data-testid="checkbox-privacy" />
                  <div className="text-sm">
                    <div className="font-medium text-slate-900">Data privacy agreement <span className="text-red-500">*</span></div>
                    <div className="text-slate-600 text-xs mt-0.5">I consent to the platform's privacy policy.</div>
                  </div>
                </label>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-between mt-6">
          <Button variant="ghost" onClick={back} disabled={step === 0} data-testid="btn-wizard-back">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={next} disabled={!stepValid()} className="bg-slate-900 hover:bg-slate-800 disabled:opacity-50" data-testid="btn-wizard-next">
              Continue <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          ) : (
            <Button onClick={submit} disabled={busy || !stepValid()} className="bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50" data-testid="btn-wizard-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit application"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div>
      <Label className="text-slate-800">{label} {required && <span className="text-red-500">*</span>}</Label>
      {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
